import type { FormEvent } from 'react'
import type { Advisor } from '@/types'

import Link from 'next/link'
import {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react'
import { CalendarPlus, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { downloadCallInvite } from '@/lib/ics'
import { formatTime } from '@/lib/format'
import {
    formatDayLabel,
    fromDateKey,
    TIMEZONE_LABEL,
    upcomingDays
} from '@/lib/schedule'
import { useMounted } from '@/hooks/use-mounted'
import { useAuth } from '@/components/generals/auth-provider'
import { PillButton } from '@/components/generals/pill-button'
import {
    Field,
    FormMessage,
    Honeypot,
    SelectInput,
    TextArea,
    TextInput
} from '@/components/generals/form-fields'

interface BookingPanelProps {
    advisor: Advisor
}

interface Confirmation {
    id: string
    advisorName: string
    date: string
    time: string
    format: string
}

type BookedSlot = { date: string, time: string }

const firstName = (name: string): string => name.split(' ')[0] ?? name

export const BookingPanel = ({ advisor }: BookingPanelProps) => {
    const { member } = useAuth()
    const mounted = useMounted()
    const days = useMemo(() => (mounted ? upcomingDays(advisor.availableDays, new Date(), 10) : []), [mounted, advisor.availableDays])
    const formats = advisor.meetingFormats.length > 0 ? advisor.meetingFormats : ['Video']
    const topics = [...advisor.focusAreas, 'Something not listed']

    const [booked, setBooked] = useState<Array<BookedSlot>>([])
    const [dateKey, setDateKey] = useState<string | null>(null)
    const [time, setTime] = useState<string | null>(null)
    const [format, setFormat] = useState(formats[0])
    const [name, setName] = useState('')
    const [email, setEmail] = useState('')
    const [topic, setTopic] = useState(topics[0])
    const [notes, setNotes] = useState('')
    const [website, setWebsite] = useState('')
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [confirmation, setConfirmation] = useState<Confirmation | null>(null)

    const loadBooked = useCallback(async () => {
        try {
            const response = await fetch(`/api/advisors/${advisor.slug}/availability`, { cache: 'no-store' })
            const data = await response.json().catch(() => null)

            if (response.ok && Array.isArray(data?.booked)) setBooked(data.booked)
        }
        catch {
            setBooked([])
        }
    }, [advisor.slug])

    useEffect(() => {
        loadBooked()
    }, [loadBooked])

    useEffect(() => {
        if (!dateKey && days.length > 0) setDateKey(days[0].key)
    }, [days, dateKey])

    useEffect(() => {
        if (!member) return

        setName(current => current || member.name)
        setEmail(current => current || member.email)
    }, [member])

    const isTaken = useCallback((day: string, slot: string) => booked.some(item => item.date === day && item.time === slot), [booked])

    useEffect(() => {
        if (dateKey && time && isTaken(dateKey, time)) setTime(null)
    }, [dateKey, time, isTaken])

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (!dateKey || !time) {
            setError('Pick a day and a time before you confirm.')
            return
        }

        setSubmitting(true)
        setError(null)

        try {
            const response = await fetch('/api/bookings', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ advisorSlug: advisor.slug, date: dateKey, time, format, name, email, topic, notes, website })
            })
            const data = await response.json().catch(() => ({}))

            if (!response.ok) {
                setError(data?.message ?? 'That booking did not go through. Please try once more.')

                if (response.status === 409) {
                    setTime(null)
                    loadBooked()
                }

                return
            }

            setConfirmation(data?.booking ?? { id: `${advisor.slug}-${dateKey}-${time}`, advisorName: advisor.name, date: dateKey, time, format })
            loadBooked()
        }
        catch {
            setError('Our calendar is out of reach right now. Check your connection and try again.')
        }
        finally {
            setSubmitting(false)
        }
    }

    const reset = () => {
        setConfirmation(null)
        setTime(null)
        setNotes('')
    }

    if (confirmation) {
        const day = fromDateKey(confirmation.date)

        return (
            <div className='glass-dark grain relative overflow-hidden rounded-[30px] p-6 text-center sm:p-8' aria-live='polite'>
                <div className='mx-auto grid size-14 place-items-center rounded-full bg-citron text-black shadow-[0_0_40px_rgba(226,243,122,0.45)]'>
                    <Check aria-hidden='true' className='size-6' />
                </div>

                <h3 className='mt-6 font-serif text-4xl italic text-cream'>
                    See you then
                </h3>

                <p className='mx-auto mt-3 max-w-xs text-[15px] leading-relaxed text-cream/75'>
                    Your {confirmation.format.toLowerCase()} call with {confirmation.advisorName} is set for {day ? formatDayLabel(day) : confirmation.date} at {formatTime(confirmation.time)} {TIMEZONE_LABEL}.
                </p>

                <p className='mx-auto mt-3 max-w-xs text-sm leading-relaxed text-cream/50'>
                    {firstName(confirmation.advisorName)}&apos;s team has the details and will reach out at {email} before you meet.
                </p>

                <div className='mt-8 flex flex-col items-center gap-3'>
                    <PillButton onClick={() => downloadCallInvite(confirmation)} size='md'>
                        Save to my calendar
                    </PillButton>

                    <button type='button' onClick={reset} className='text-sm text-cream/60 underline decoration-cream/30 underline-offset-[6px] hover:text-cream'>
                        Schedule another call
                    </button>
                </div>

                {!member && (
                    <p className='mt-8 border-t border-white/10 pt-6 text-xs leading-relaxed text-cream/50'>
                        To view or cancel your calls in one place,{' '}
                        <Link href='/login?mode=signup&next=/account' className='text-cream underline decoration-cream/30 underline-offset-4 hover:decoration-citron'>
                            open a free account
                        </Link>
                        {' '}using this same email.
                    </p>
                )}
            </div>
        )
    }

    return (
        <form onSubmit={submit} noValidate className='glass-dark grain relative overflow-hidden rounded-[30px] p-5 sm:p-7'>
            <div className='flex items-start justify-between gap-4'>
                <div>
                    <h2 className='font-serif text-[2rem] italic leading-tight text-cream'>
                        A free first call
                    </h2>

                    <p className='mt-1 text-sm text-cream/55'>
                        Half an hour with {firstName(advisor.name)}. All times in {TIMEZONE_LABEL}.
                    </p>
                </div>

                <CalendarPlus aria-hidden='true' className='mt-2 size-5 shrink-0 text-citron' />
            </div>

            <fieldset className='mt-6'>
                <legend className='text-[11px] font-medium uppercase tracking-[0.24em] text-cream/50'>
                    Date
                </legend>

                <div className='no-scrollbar -mx-1 mt-3 flex snap-x gap-2 overflow-x-auto px-1 pb-1'>
                    {days.length === 0 && Array.from({ length: 5 }, (_, index) => (
                        <span key={index} className='skeleton h-[74px] w-[64px] shrink-0 rounded-2xl' />
                    ))}

                    {days.map(day => {
                        const selected = day.key === dateKey
                        const full = advisor.timeSlots.every(slot => isTaken(day.key, slot))

                        return (
                            <button
                                key={day.key}
                                type='button'
                                disabled={full}
                                aria-pressed={selected}
                                onClick={() => {
                                    setDateKey(day.key)
                                    setTime(null)
                                }}
                                className={cn(
                                    'flex w-[64px] shrink-0 snap-start flex-col items-center rounded-2xl py-2.5 text-center transition-all duration-300',
                                    selected ? 'bg-cream text-black' : 'bg-white/[0.05] text-cream ring-1 ring-white/10 hover:ring-white/25',
                                    full && 'opacity-35'
                                )}
                            >
                                <span className={cn('text-[10px] uppercase tracking-[0.18em]', selected ? 'text-black/60' : 'text-cream/50')}>
                                    {day.date.toLocaleDateString('en-US', { weekday: 'short' })}
                                </span>

                                <span className='mt-0.5 text-xl font-medium tabular-nums leading-none'>
                                    {day.date.getDate()}
                                </span>

                                <span className={cn('mt-1 text-[10px]', selected ? 'text-black/60' : 'text-cream/45')}>
                                    {day.date.toLocaleDateString('en-US', { month: 'short' })}
                                </span>
                            </button>
                        )
                    })}
                </div>
            </fieldset>

            <fieldset className='mt-5'>
                <legend className='text-[11px] font-medium uppercase tracking-[0.24em] text-cream/50'>
                    Start time
                </legend>

                <div className='mt-3 grid grid-cols-3 gap-2'>
                    {advisor.timeSlots.map(slot => {
                        const taken = dateKey ? isTaken(dateKey, slot) : false
                        const selected = slot === time

                        return (
                            <button
                                key={slot}
                                type='button'
                                disabled={taken || !dateKey}
                                aria-pressed={selected}
                                onClick={() => setTime(slot)}
                                className={cn(
                                    'h-11 rounded-full text-[13px] tabular-nums transition-all duration-300',
                                    selected ? 'bg-cream text-black' : 'bg-white/[0.05] text-cream ring-1 ring-white/10 hover:ring-white/25',
                                    taken && 'text-cream/30 line-through'
                                )}
                            >
                                {formatTime(slot)}
                            </button>
                        )
                    })}
                </div>
            </fieldset>

            {formats.length > 1 && (
                <fieldset className='mt-5'>
                    <legend className='text-[11px] font-medium uppercase tracking-[0.24em] text-cream/50'>
                        Meet by
                    </legend>

                    <div className='mt-3 flex rounded-full bg-white/[0.05] p-1 ring-1 ring-white/10'>
                        {formats.map(option => (
                            <button
                                key={option}
                                type='button'
                                aria-pressed={format === option}
                                onClick={() => setFormat(option)}
                                className={cn(
                                    'h-9 flex-1 rounded-full text-[13px] transition-colors duration-300',
                                    format === option ? 'bg-cream text-black' : 'text-cream/65 hover:text-cream'
                                )}
                            >
                                {option}
                            </button>
                        ))}
                    </div>
                </fieldset>
            )}

            <div className='mt-6 space-y-4'>
                <Field id='booking-name' label='Full name'>
                    <TextInput id='booking-name' name='name' autoComplete='name' required value={name} onChange={event => setName(event.target.value)} />
                </Field>

                <Field id='booking-email' label='Email address'>
                    <TextInput id='booking-email' name='email' type='email' autoComplete='email' required value={email} onChange={event => setEmail(event.target.value)} />
                </Field>

                <Field id='booking-topic' label='What should the call cover?'>
                    <SelectInput id='booking-topic' name='topic' value={topic} onChange={event => setTopic(event.target.value)}>
                        {topics.map(option => <option key={option} value={option}>{option}</option>)}
                    </SelectInput>
                </Field>

                <Field id='booking-notes' label='Anything worth knowing in advance?' optional>
                    <TextArea id='booking-notes' name='notes' rows={3} maxLength={1000} className='min-h-[88px]' value={notes} onChange={event => setNotes(event.target.value)} />
                </Field>
            </div>

            <Honeypot value={website} onChange={setWebsite} />

            {error && <FormMessage className='mt-5'>{error}</FormMessage>}

            <div className='mt-6 flex flex-wrap items-center justify-between gap-4'>
                <p className='text-xs text-cream/45'>
                    {dateKey && time
                        ? `${fromDateKey(dateKey) ? formatDayLabel(fromDateKey(dateKey) as Date) : dateKey}, ${formatTime(time)} ${TIMEZONE_LABEL}`
                        : 'No time chosen yet'}
                </p>

                <PillButton type='submit' loading={submitting} disabled={!dateKey || !time}>
                    Book this time
                </PillButton>
            </div>
        </form>
    )
}
